/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';
import { notEmpty } from '@ember/object/computed';
import { task } from 'ember-concurrency';
import { REVERSE_PERIOD_KEYS } from 'cio/utils/metrics/common';
import { SEGMENT_MEMBERSHIP_RELEASE_TIMESTAMP } from 'cio/utils/metrics/segment-membership';
import moment from 'moment-timezone';
import MetricsService from 'cio/services/metrics';
import { SegmentMembershipMetric } from 'cio/utils/metrics/models/segment-membership-metric';
import { tracked } from '@glimmer/tracking';
import { Grouping } from 'cio/utils/metrics/resolution';
import type { Options } from 'highcharts';
import { TimeRange } from 'cio/utils/time';

interface SegmentMembershipSparkLineChartArgs {
  segmentId: number | null;
  showDiff?: boolean;
  timeRange?: TimeRange | null;
  grouping?: Grouping;
  isLoading?: boolean;
  onDataFetched?: (metric?: SegmentMembershipMetric) => unknown;
  showNoPeopleNotice?: boolean;
  noPeopleNotice?: string;
  showNoPeopleLink?: boolean;
  chartOptions?: Options;
  onTrackNavigate?: () => unknown;
}

export default class SegmentMembershipSparkLineChart extends Component<SegmentMembershipSparkLineChartArgs> {
  @service('metrics') metricsService!: MetricsService;

  @tracked failedToLoad = false;

  constructor(owner: unknown, args: SegmentMembershipSparkLineChartArgs) {
    super(owner, args);

    this.fetchMembershipMetrics.perform();
  }

  willDestroy() {
    super.willDestroy();

    this.fetchMembershipMetrics.cancelAll({ resetState: true });
  }

  @action
  onSegmentChanged() {
    this.failedToLoad = false;
    this.fetchMembershipMetrics.cancelAll({ resetState: true });
    this.fetchMembershipMetrics.perform();
  }

  get showNoPeopleNotice() {
    return this.args.showNoPeopleNotice ?? false;
  }

  get showNoPeopleLink() {
    return this.args.showNoPeopleLink ?? false;
  }

  get showDiff() {
    return this.args.showDiff ?? false;
  }

  get diff() {
    let data = this.membershipMetrics?.total ?? [];

    if (data.length === 0) {
      return 0;
    }

    let [firstPoint] = data;
    let lastPoint = data[data.length - 1];

    return (lastPoint.y ?? 0) - (firstPoint.y ?? 0);
  }

  get diffSign() {
    // We don't need to return '-' for negative numbers, since the - is included
    // in the number itself already
    if (this.diff >= 0) {
      return '+';
    }

    return '';
  }

  @computed('fetchMembershipMetrics.isRunning', 'args.isLoading')
  get isLoading() {
    return (this.args.isLoading ?? false) || this.fetchMembershipMetrics.isRunning;
  }

  @computed('fetchMembershipMetrics.lastSuccessful.value')
  get membershipMetrics(): null | SegmentMembershipMetric {
    return this.fetchMembershipMetrics?.lastSuccessful?.value;
  }

  @notEmpty('membershipMetrics') hasMetrics!: boolean;

  get isAllZeroesOrEmpty() {
    let seriesData = this.membershipMetrics?.total ?? [];

    return seriesData.length === 0 || seriesData.every((p) => p.y === 0);
  }

  get noSegmentOrFailed() {
    return isNone(this.args.segmentId) || this.failedToLoad;
  }

  fetchMembershipMetrics = task({ drop: true }, async () => {
    this.failedToLoad = false;

    if (isNone(this.args.segmentId)) {
      return null;
    }

    let end = moment.utc().startOf('day');
    // We subtract 29 days to have a total of 30 days' worth of data
    let start = end.clone().subtract(29, 'days');

    if (this.args.timeRange?.start && this.args.timeRange?.end) {
      start = moment.tz(`${this.args.timeRange.start.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC');
      end = moment.tz(`${this.args.timeRange.end.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC');
    }

    let period = REVERSE_PERIOD_KEYS[this.args.grouping ?? Grouping.Daily];

    if (start.isBefore(moment.unix(SEGMENT_MEMBERSHIP_RELEASE_TIMESTAMP))) {
      start = moment.unix(SEGMENT_MEMBERSHIP_RELEASE_TIMESTAMP);
    }

    try {
      let result = await this.metricsService.fetchSegmentMembershipMetrics(this.args.segmentId, period, {
        start,
        end,
      });

      if (this.args.onDataFetched) {
        this.args.onDataFetched(result);
      }

      return result;
    } catch {
      if (this.args.onDataFetched) {
        this.args.onDataFetched();
      }
      this.failedToLoad = true;
    }
  });

  @action
  onTrackGoToSegment() {
    if (this.args.onTrackNavigate) {
      this.args.onTrackNavigate();
    }
  }
}
